import type { DueItem, DueTarget } from "@kundeninfo/api-client";
import { bezugAusAkteZiel } from "../../components/objektPanel/objektBezug.js";
import type { AkteZielBezug } from "../../components/objektPanel/objektBezug.js";
import { tageZwischen } from "../tickets/ticketFormat.js";
import { DUE_ITEM_TEXT } from "./dueItemTexts.js";

/**
 * DIE TERMINEBENE DER OBERFLÄCHE (Spec „Oberfläche“, D10, D13, D22, D26; Plan T17) — reine Funktionen, die Liste,
 * Zeitachse und Kalender teilen: Quellentabelle und was je Ort angeboten wird, Ordnung wie die Domäne
 * (`compareDueItems` in `packages/domain/src/dueItemRules.ts`; `web` importiert `domain` nicht), Doppelte (D26),
 * Kennzeichen und Betrag. Die Richtung der Importe ist `tickets` → `termine`: Hier stehen nur Blätter aus `tickets`
 * (`ticketFormat.ts`), nie Ansichten.
 */

/** Die Quellenfolge (D10) — Folge der Chips, Ordnung bei gleichem Tag. */
export const DUE_SOURCES = ["TICKET", "MILESTONE", "OFFER", "INVOICE", "PLANNED_CHARGE", "SUPPLIER_PAYMENT", "SUPPLIER_NOTICE"] as const;
export type DueSource = (typeof DUE_SOURCES)[number];
/** Die Fremdquellen, aus denen ein Fälligkeitsticket entsteht (Server: `DUE_TICKET_SOURCES`); TICKET und MILESTONE nie. */
export const DUE_TICKET_SOURCES = ["OFFER", "INVOICE", "PLANNED_CHARGE", "SUPPLIER_PAYMENT", "SUPPLIER_NOTICE"] as const satisfies readonly DueSource[];
/** Nur mit Lieferantensicht (D12, Entscheid 496). */
export const SUPPLIER_SOURCES: readonly DueSource[] = ["SUPPLIER_PAYMENT", "SUPPLIER_NOTICE"];
/** Was die Oberfläche anbietet (T17): seit Stufe B (Aufgabe 8) alle Quellen, auch „Meilensteine“ — in Stufe A fehlte der Chip
 *  hier (Ruling 7). Die eine Stelle, an der eine Quelle zurückgehalten würde. */
export const OFFERED_SOURCES: readonly DueSource[] = DUE_SOURCES;
/** Wo die Chips stehen: Liste und Zeitachse zeigen keinen Chip „Tickets“ (ihre Tabelle bzw. Balken sind die Tickets). */
export type DueContext = "LIST" | "TIMELINE" | "CALENDAR";

const RANK: Readonly<Record<DueItem["state"], number>> = { OVERDUE: 0, DUE_TODAY: 1, OPEN: 2, DONE: 3 };

export function isDueSource(s: string): s is DueSource {
  return (DUE_SOURCES as readonly string[]).includes(s);
}

/** Die Chips eines Orts in der Quellenfolge (D22). */
export function chipSources(ctx: DueContext, supplierView: boolean): DueSource[] {
  return OFFERED_SOURCES.filter((s) => (ctx === "CALENDAR" || s !== "TICKET") && (supplierView || !SUPPLIER_SOURCES.includes(s)));
}

/** Was ein Ort von der gemerkten Wahl anfragt: nur seine Chips, in der Quellenfolge. Leer heißt „nicht anfragen“ — der
 *  Klient machte aus `sources: []` „alle Quellen“ (Review Aufgabe 2, H3). */
export function requestSources(chosen: readonly DueSource[], ctx: DueContext, supplierView: boolean): DueSource[] {
  return chipSources(ctx, supplierView).filter((s) => chosen.includes(s));
}

/** D13: Einträge unbekannter Quellen (künftig `OPEN_ITEM`, `APPOINTMENT`) fallen weg, statt die Ansicht zu stören. */
export function knownItems(items: readonly DueItem[]): DueItem[] {
  return items.filter((i) => isDueSource(i.source));
}

/** G8: Was eine Ansicht zeichnet — nur Einträge der angefragten Quellen (die immer bekannt sind, D13). So verschwindet eine
 *  abgeschaltete Quelle in Liste, Zeitachse und Kalender sofort, nicht erst mit der nächsten Antwort. */
export function requestedItems(items: readonly DueItem[], sources: readonly string[]): DueItem[] {
  return items.filter((i) => sources.includes(i.source));
}

/** Gesetzt vor `null`, dann aufsteigend. */
function setFirst(a: string | null, b: string | null, cmp: (x: string, y: string) => number): number {
  if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
  return cmp(a, b);
}
const plain = (x: string, y: string): number => (x < y ? -1 : x > y ? 1 : 0);
const natural = (x: string, y: string): number => x.localeCompare(y, "de", { numeric: true });
const sourceIndex = (s: string): number => DUE_SOURCES.indexOf(s as DueSource);

/** D10 wie die Domäne: Rang („Schlechtestes oben“), Tag, Uhrzeit (ohne zuletzt), Quellenfolge, Nummer, Schlüssel. */
export function compareDueItems(a: DueItem, b: DueItem): number {
  return RANK[a.state] - RANK[b.state] || plain(a.date, b.date) || setFirst(a.time, b.time, plain)
    || sourceIndex(a.source) - sourceIndex(b.source) || setFirst(a.reference, b.reference, natural) || plain(a.key, b.key);
}

/** D26: Das sichtbare Fälligkeitsticket liegt am Fälligkeitstag des Objekts. Nur dann tritt der Eintrag dort zurück,
 *  wo das Ticket steht; weicht der Tag ab (von Hand umgeplant), bleiben beide und das Objekt nennt das Ticket. */
export function sameDayTicket(item: DueItem): boolean {
  return item.ticket !== null && item.ticket.dueDate === item.date;
}

/** „Ticket anlegen“ (Aufgabe 12) gilt Fremdeinträgen ohne Fälligkeitsticket — nie einem Meilenstein oder Ticket. */
export function canCreateTicket(item: DueItem): boolean {
  return item.ticket === null && (DUE_TICKET_SOURCES as readonly string[]).includes(item.source);
}

/** Die Tickets, deren Fälligkeit in DIESER Antwort steht (Review Aufgabe 2, H2): Das Fälligkeitsticket eines Objekts
 *  kann archiviert oder erledigt sein und fehlt dann in der Antwort. */
export function ticketDueIds(items: readonly DueItem[]): Set<string> {
  return new Set(items.filter((i) => i.source === "TICKET" && i.dateKind === "DUE").map((i) => i.objectId));
}

/** D26 mit H2: Der Eintrag tritt nur zurück, wenn sein Ticket am selben Tag liegt UND dort gezeigt wird. */
export function recedesBehindTicket(item: DueItem, shownTicketIds: ReadonlySet<string>): boolean {
  return sameDayTicket(item) && shownTicketIds.has(item.ticket!.id);
}

/** Kalendertag + n Tage (`2026-10-17` + 14 = `2026-10-31`), gerechnet in UTC wie `ticketFormat.ts`. */
export function plusDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + n)).toISOString().slice(0, 10);
}

/** Kennzeichen „Fällig“ wie „Fällig bis“ (R15): Fehlerton überschritten, Warnton heute, sonst neutral. */
export function dueMark(item: DueItem, heute: string): { art: "fehler" | "warnung" | null; text: string } {
  switch (item.state) {
    case "OVERDUE": return { art: "fehler", text: DUE_ITEM_TEXT.overdueSince(tageZwischen(item.date, heute)) };
    case "DUE_TODAY": return { art: "warnung", text: DUE_ITEM_TEXT.dueToday };
    case "OPEN": return { art: null, text: DUE_ITEM_TEXT.open };
    case "DONE": return { art: null, text: DUE_ITEM_TEXT.done };
  }
}

/** „1.190,00 € brutto“ (Leerzeichen vor € ist U+00A0 wie im Server); ohne Betrag `null`. Gesetzt wird er rechtsbündig. */
export function amountText(item: Pick<DueItem, "amount" | "currency" | "amountBasis">): string | null {
  return DUE_ITEM_TEXT.amount(item);
}

/** Ein Termin mit Ziel in der Akte als Bezug (PF4, Spec A6/A7): Liefervertrag und Eingangsrechnung öffnen als Panel, Belege
 *  springen in ihren Reiter (`oeffneOderSpringe`). Beschriftung: Nummer bzw. Titel des Eintrags. */
export function terminBezug(t: Extract<DueTarget, { kind: "RECORD" }>, item: Pick<DueItem, "reference" | "title">): AkteZielBezug {
  return bezugAusAkteZiel({ customerId: t.customerId, tab: t.tab, objectId: t.objectId, companyId: t.companyId },
    item.reference ?? item.title);
}
